/**
 * County-level recording rules.
 *
 * These are the office-specific filing rules that decide whether a recorded
 * easement is accepted or bounced back: how the office structures its fees,
 * what the document has to look like, whether it takes electronic submissions,
 * and where the free official index and parcel viewer live.
 *
 * Every entry is transcribed from the county's own published pages. Dollar
 * amounts are deliberately omitted (schedules change; the linked schedule is
 * the authority) unless a state statute fixes the amount. Counties without a
 * researched profile fall back to the state recording baseline rather than
 * being filled with plausible-sounding guesses.
 *
 * Free official sources only — no paid data provider.
 */

export interface RecordingRuleSource {
  label: string;
  url: string;
}

export interface CountyRecordingRule {
  /** Matches CountyEasementGuide.slug. */
  slug: string;
  stateCode: string;
  /** Exact office name as the county publishes it. */
  office: string;
  /** One sentence on how the office structures recording fees. */
  feeStructure: string;
  /** Formatting requirements that get an easement rejected at the counter. */
  formatting: string[];
  /** Whether the office accepts electronic recording through submitters. */
  eRecording: boolean;
  /** Sentence describing submission channels (e-record, mail, walk-in). */
  submission: string;
  /** Free public parcel / GIS viewer, when the county publishes one. */
  parcelViewerUrl?: string;
  /** Official recorded-document index search. */
  indexUrl?: string;
  /** Official published fee schedule. */
  feeScheduleUrl?: string;
  /** A genuinely local quirk that affects easement filings here. */
  quirk?: string;
  /** Official pages the entry was transcribed from. */
  sources: RecordingRuleSource[];
}

export const COUNTY_RECORDING_RULES: CountyRecordingRule[] = [
  {
    slug: "clark",
    stateCode: "NV",
    office: "Clark County Recorder",
    feeStructure:
      "Clark County charges a flat fee per standard document under NRS 247.305, so a twelve-page easement with exhibits costs the same to record as a one-page one. Maps and UCC filings run on their own schedules.",
    formatting: [
      "Letter-size white paper, printed on one side only, nothing stapled through or physically attached.",
      "One-inch blank margins on the left, right and bottom of every page, plus a blank three-inch by three-inch box in the top-right corner of page one.",
      "Black ink at ten point or larger, no more than nine lines of text per vertical inch, and no stamp or seal overlapping text or signatures.",
      "The eleven-digit Assessor's Parcel Number goes at the top left of page one, and a deed or grant must be acknowledged or proved as NRS 247.120(1)(a) requires, which in practice means a notary.",
    ],
    eRecording: true,
    submission:
      "Las Vegas filings go to the Recorder in downtown Las Vegas, by mail, or electronically through an authorized e-recording submitter. The Recorder publishes a sample document showing exactly where the margins and parcel number belong.",
    parcelViewerUrl:
      "https://maps.clarkcountynv.gov/assessor/AssessorParcelDetail/RealPropertySearchLanding.aspx",
    indexUrl: "https://maps.clarkcountynv.gov/assessor/webimages/default.asp",
    feeScheduleUrl:
      "https://www.clarkcountynv.gov/government/elected_officials/county_recorder/fees",
    quirk:
      "Nearly every conveyance recorded in Clark County needs a Declaration of Value form attached for real property transfer tax purposes. An easement grant for consideration is routinely bounced back without one.",
    sources: [
      {
        label: "Clark County Recorder. Recordation process",
        url: "https://www.clarkcountynv.gov/government/elected_officials/county_recorder/recordation-process",
      },
      {
        label: "Clark County Recorder. Document requirements sample",
        url: "https://www.clarkcountynv.gov/assets/documents/government/elected_officials/county_recorder/requirements-sample.pdf",
      },
      {
        label: "Clark County Recorder. Fees",
        url: "https://www.clarkcountynv.gov/government/elected_officials/county_recorder/fees",
      },
    ],
  },
  {
    slug: "maricopa",
    stateCode: "AZ",
    office: "Maricopa County Recorder",
    feeStructure:
      "Arizona sets a flat fee per document, so page count does not change the cost of a standard easement. Plats and surveys are the exception and are charged by the sheet.",
    formatting: [
      "Paper no larger than eight and a half by fourteen inches, printed at ten point or larger, per A.R.S. § 11-480.",
      "At least a half-inch margin on every side throughout; anything printed inside the margin does not impart notice.",
      "Page one needs a two-inch top margin reserved for the recording stamp, with the left three and a half inches of that band kept clear.",
      "A caption at the top stating the type of instrument, plus original signatures and legible text for certified copies.",
    ],
    eRecording: true,
    submission:
      "Maricopa runs its own digital recording program under an account agreement, and the large majority of documents now arrive that way. Counter and mail filing remain available.",
    parcelViewerUrl: "https://maps.mcassessor.maricopa.gov/",
    indexUrl: "https://recorder.maricopa.gov/recording/document-search.html",
    feeScheduleUrl: "https://recorder.maricopa.gov/recording/recording-fees.html",
    quirk:
      "Because the fee is flat, Phoenix-area easements are often recorded with the full survey exhibit attached rather than a shortened description. Useful when you are trying to locate the strip years later.",
    sources: [
      {
        label: "Maricopa County Recorder. Form requirements",
        url: "https://recorder.maricopa.gov/recording/form-requirements.html",
      },
      {
        label: "Maricopa County Recorder. Recording fees",
        url: "https://recorder.maricopa.gov/recording/recording-fees.html",
      },
      {
        label: "Maricopa County Recorder. Digital recording",
        url: "https://recorder.maricopa.gov/recorder/digitalrecording.aspx",
      },
    ],
  },
  {
    slug: "pima",
    stateCode: "AZ",
    office: "Pima County Recorder",
    feeStructure:
      "Pima charges the same statewide flat fee per document as the rest of Arizona, with plats and surveys billed by the sheet. The Recorder publishes the current amounts.",
    formatting: [
      "Arizona's statewide format rules under A.R.S. § 11-480 apply: maximum eight and a half by fourteen inch paper and ten point or larger type.",
      "Half-inch margins on all sides, with a two-inch reserved band across the top of the first page.",
      "A caption naming the instrument and original signatures, as A.R.S. § 11-480 requires.",
    ],
    eRecording: true,
    submission:
      "Pima operates a self-service online recording portal for search, submission and payment, alongside counter filing; photo identification is required for in-person recording under A.R.S. § 11-472.",
    parcelViewerUrl: "https://gis.pima.gov/maps/landbase/parsrch.htm",
    indexUrl: "https://www.recorder.pima.gov/PublicSearch",
    feeScheduleUrl: "https://www.recorder.pima.gov/fees",
    sources: [
      { label: "Pima County Recorder. ERecording", url: "https://recorder.pima.gov/eRecording" },
      { label: "Pima County Recorder. Fees", url: "https://www.recorder.pima.gov/fees" },
    ],
  },
  {
    slug: "harris",
    stateCode: "TX",
    office: "Harris County Clerk, Real Property Department",
    feeStructure:
      "Harris County charges for the first page and then a lower amount for each additional page, plus a per-name charge once a document indexes more than five names. Survey exhibits count as pages.",
    formatting: [
      "The grantee's mailing address must appear in the instrument or an attached writing under Texas Property Code § 11.003, or a penalty fee is added.",
      "A clear identifying caption at the top of the first page, under Texas Local Government Code § 191.007.",
      "Names typed or printed legibly beneath every signature.",
      "Original signature and a proper notarial acknowledgment. A copy will not record.",
    ],
    eRecording: true,
    submission:
      "The Clerk accepts documents by mail, at the counter, and electronically through established e-filing service providers.",
    parcelViewerUrl: "https://arcweb.hcad.org/parcel-viewer-v2.0/",
    indexUrl: "https://cclerk.hctx.net/Applications/WebSearch/RP.aspx",
    feeScheduleUrl: "https://www.cclerk.hctx.net/RealProperty.aspx",
    quirk:
      "The Clerk's online real property database carries redactions and a published notice that racially restrictive covenant language in older Houston-area instruments is void and unenforceable. The official paper record may read differently from the online image.",
    sources: [
      {
        label: "Harris County Clerk. Real property recording",
        url: "https://www.cclerk.hctx.net/RealProperty.aspx",
      },
      {
        label: "Harris County Clerk. Real property search",
        url: "https://cclerk.hctx.net/Applications/WebSearch/RP.aspx",
      },
    ],
  },
  {
    slug: "travis",
    stateCode: "TX",
    office: "Travis County Clerk, Recording Division",
    feeStructure:
      "A first-page charge that bundles the recording, records-management and archive fees, then a per-page charge for every additional page and a per-name charge past five names.",
    formatting: [
      "Leave at least three inches of blank space at the bottom of the last page or the Clerk charges for an extra page.",
      "Documents that miss the Local Government Code § 191.007 format specifications are charged double the regular recording fee, but the clerk may not refuse them for format failures alone.",
      "The grantee's address is required under Property Code § 11.003; leaving it out adds a penalty.",
      "Originals with original signatures only. The Recording Division will not record a copy.",
    ],
    eRecording: true,
    submission:
      "Austin filings can be walked in or mailed. Electronic recording is limited by Local Government Code § 195.003 to defined submitters: attorneys, banks and savings institutions, federally chartered lenders, licensed lenders, title companies and agents, state agencies and municipal clerks; a county of 500,000 or more may authorize others by agreement.",
    parcelViewerUrl: "https://traviscad.org/propertysearch/",
    indexUrl: "https://countyclerk.traviscountytx.gov/departments/recording/real-property/",
    feeScheduleUrl:
      "https://countyclerk.traviscountytx.gov/departments/recording/fee-information/",
    quirk:
      "Travis County will not put the recording sticker on the last page of a certified copy, so every certified copy of a recorded easement picks up an extra page charge.",
    sources: [
      {
        label: "Travis County Clerk. Real property recording",
        url: "https://countyclerk.traviscountytx.gov/departments/recording/real-property/",
      },
      {
        label: "Travis County Clerk. Fee information",
        url: "https://countyclerk.traviscountytx.gov/departments/recording/fee-information/",
      },
    ],
  },
  {
    slug: "tarrant",
    stateCode: "TX",
    office: "Tarrant County Clerk, Real Estate Records",
    feeStructure:
      "A first-page charge plus a per-page charge for each additional page on most instruments; plats and UCC filings run on separate schedules.",
    formatting: [
      "The document must be facially regular, properly executed, acknowledged and notarized, before Records Intake will accept it.",
      "Only instruments a statute authorizes, requires or permits to be recorded are accepted.",
      "Texas Local Government Code § 191.007 caption and legibility rules apply, as they do statewide.",
    ],
    eRecording: true,
    submission:
      "Tarrant has taken electronic filings since 2004 and now records the large majority of land documents that way, either through a third-party e-recording company or as a directly authorized submitter. Counter and mail filing remain open in Fort Worth.",
    parcelViewerUrl: "https://gisit.tarrantcounty.com/PublicMapViewer/",
    indexUrl: "https://tarrant.tx.publicsearch.us/",
    feeScheduleUrl:
      "https://www.tarrantcountytx.gov/en/county-clerk/real-estate-records/fee-schedules.html",
    sources: [
      {
        label: "Tarrant County Clerk. Fee schedules",
        url: "https://www.tarrantcountytx.gov/en/county-clerk/real-estate-records/fee-schedules.html",
      },
      {
        label: "Tarrant County Clerk. ERecording",
        url: "https://www.tarrantcountytx.gov/en/county-clerk/real-estate-records/erecording.html",
      },
      {
        label: "Tarrant County Clerk. Records intake FAQs",
        url: "https://www.tarrantcountytx.gov/en/county-clerk/real-estate-records/records-intake-faqs--frequently-asked-questions-.html",
      },
    ],
  },
  {
    slug: "miami-dade",
    stateCode: "FL",
    office: "Miami-Dade Clerk of the Circuit Court and Comptroller, Official Records",
    feeStructure:
      "Charged by page count and by the number of indexed names; the Clerk publishes a recorder calculator that prices a filing before you send it.",
    formatting: [
      "A blank three-inch by three-inch space in the top-right corner of page one and a one-inch by three-inch space on later pages, under Fla. Stat. § 695.26(1)(e).",
      "A preparer and return-address block, carried on the Clerk's cover page when the document itself lacks the reserved space.",
      "Notarial acknowledgment, and two witnesses whose names and post office addresses must be legibly printed on real property instruments since January 1, 2024 under Fla. Stat. § 695.26.",
    ],
    eRecording: true,
    submission:
      "Miami filings are recorded at the Clerk's Official Records office or submitted through the Clerk's online services; a mandatory cover page is supplied when the document lacks the reserved recording space.",
    parcelViewerUrl: "https://gisweb.miamidade.gov/landinformation/",
    indexUrl: "https://www.miamidadeclerk.gov/clerk/official-records.page",
    feeScheduleUrl: "https://onlineservices.miamidadeclerk.gov/officialrecords/recordercalculator",
    quirk:
      "If your easement does not leave the reserved blank block, the Clerk adds its own cover page. And charges for that extra page.",
    sources: [
      {
        label: "Miami-Dade Clerk. Official records",
        url: "https://www.miamidadeclerk.gov/clerk/official-records.page",
      },
      {
        label: "Miami-Dade Clerk. Document cover page",
        url: "https://www.miamidadeclerk.gov/library/county_recorder/155-web.pdf",
      },
      {
        label: "Miami-Dade Clerk. Fee schedule",
        url: "https://onlineservices.miamidadeclerk.gov/officialrecords/recordercalculator",
      },
    ],
  },
  {
    slug: "broward",
    stateCode: "FL",
    office: "Broward County Records, Taxes and Treasury Division, Recording Section",
    feeStructure:
      "Priced by number of pages and number of names; the county publishes a recording fee and documentary tax calculator that takes both.",
    formatting: [
      "Grantor and grantee names printed legibly in the body, with mailing addresses.",
      "A legal description of land located in Broward County.",
      "Grantor signatures plus two witnesses, each witness's name and address printed beneath the signature under Fla. Stat. § 695.26.",
      "A complete notary acknowledgment with names, date, signature, printed name and commission expiration.",
    ],
    eRecording: true,
    submission:
      "Broward accepts electronic submissions through approved e-recording vendors, along with mail and counter filing in Fort Lauderdale.",
    parcelViewerUrl: "https://gisweb-adapters.bcpa.net/bcpawebmap_ex/bcpawebmap.aspx",
    indexUrl: "https://officialrecords.broward.org/",
    feeScheduleUrl: "https://www.broward.org/RecordsTaxesTreasury/TaxesFees/Pages/FeeSchedule.aspx",
    quirk:
      "Broward publishes the requirements as a hard checklist: a deed or easement missing any one of them cannot be recorded at all, rather than being recorded with a surcharge.",
    sources: [
      {
        label: "Broward County. Recording your deed",
        url: "https://www.broward.org/RECORDSTAXESTREASURY/RECORDS/Pages/RecordingYourDeed.aspx",
      },
      {
        label: "Broward County. Records division",
        url: "https://www.broward.org/RecordsTaxesTreasury/Records/Pages/default.aspx",
      },
      { label: "Broward County. Official records search", url: "https://officialrecords.broward.org/" },
    ],
  },
  {
    slug: "hillsborough",
    stateCode: "FL",
    office: "Hillsborough County Clerk of Circuit Court and Comptroller, Recording Department",
    feeStructure:
      "A first-page charge and a lower per-page charge for each additional page, both fixed by Florida statute, plus an indexing charge once a document carries more than four names.",
    formatting: [
      "Two witnesses with names and post office addresses legibly printed, typed or stamped on real property instruments since January 1, 2024; an email address is not accepted as the address.",
      "Documents that miss the Fla. Stat. § 695.26 requirements are refused rather than surcharged.",
      "Mailed filings need a self-addressed stamped envelope for return of the original.",
    ],
    eRecording: true,
    submission:
      "The Clerk publishes formal electronic recording business rules and accepts both scanned-image and SMART document models, as well as counter and mail filing in Tampa.",
    parcelViewerUrl: "https://gis.hcpafl.org/mobilesearch/",
    indexUrl: "https://www.hillsclerk.com/propertyrecords-and-recording",
    feeScheduleUrl: "https://www.hillsclerk.com/about-us/fees-and-fines",
    quirk:
      "The witness-address rule is the most common Tampa rejection: the Clerk is expressly authorized to refuse a real property document where a witness's post office address is missing.",
    sources: [
      {
        label: "Hillsborough Clerk. Property records and recording",
        url: "https://www.hillsclerk.com/propertyrecords-and-recording",
      },
      { label: "Hillsborough Clerk. Fees and fines", url: "https://www.hillsclerk.com/about-us/fees-and-fines" },
    ],
  },
  {
    slug: "fulton",
    stateCode: "GA",
    office: "Fulton County Clerk of Superior and Magistrate Courts, Real Estate Division",
    feeStructure:
      "Georgia sets a flat $25 statutory fee per real estate instrument with no page limit (O.C.G.A. § 15-6-77(f), effective January 1, 2020); the Clerk publishes the current schedule.",
    formatting: [
      "The tax parcel identification number is required on documents affecting real property, under Georgia House Bill 1036 effective August 27, 2018.",
      "Under O.C.G.A. § 44-2-14(b), a conveyance or encumbrance must show the name and mailing address of the person it is to be returned to at the top of page one; leaving it off does not by itself impair the recording.",
      "Plats, maps and floor plans must be filed electronically rather than on paper.",
      "The Real Estate Division does not take credit cards at the counter. Cash, business check or money order only.",
    ],
    eRecording: true,
    submission:
      "Atlanta filings go through approved e-recording vendors or over the counter; plats are e-filed through the statewide clerks' authority system.",
    parcelViewerUrl: "https://gis.fultoncountyga.gov/Apps/PropertyMapViewer/",
    indexUrl: "https://search.gsccca.org/RealEstate/",
    feeScheduleUrl: "https://www.fultonclerk.org/400/Fee-Schedule",
    quirk:
      "Georgia indexes recorded real estate statewide through the Superior Court Clerks' Cooperative Authority, so a Fulton County easement is searchable in the same free index as every other Georgia county.",
    sources: [
      { label: "Fulton County Clerk. Fee schedule", url: "https://www.fultonclerk.org/400/Fee-Schedule" },
      {
        label: "Fulton County Clerk. Real estate services",
        url: "https://www.fultonclerk.org/363/Records-Real-Estate-Services",
      },
      { label: "Fulton County Clerk. Recording division", url: "https://www.fultonclerk.org/370/Recording-Division" },
    ],
  },
  {
    slug: "wake",
    stateCode: "NC",
    office: "Wake County Register of Deeds",
    feeStructure:
      "North Carolina charges a base fee covering the first block of pages, thirty-five for deeds of trust, fifteen for other instruments, then a per-page charge beyond that, plus a surcharge for documents that miss the standard format.",
    formatting: [
      "Letter or legal size paper, printed on one side only.",
      "A three-inch blank margin across the top of the first page, with half-inch margins elsewhere.",
      "Black print on white paper at nine point or larger.",
      "The type of instrument stated at the top center of the first page.",
    ],
    eRecording: true,
    submission:
      "Raleigh filings can be e-recorded through approved submitters, mailed, or brought to the Register of Deeds. A non-standard document is recorded with an added statutory fee rather than refused.",
    parcelViewerUrl:
      "https://www.wake.gov/departments-government/geographic-information-services-gis/maps-apps-data/imaps-information",
    indexUrl: "https://rodcrpi.wakegov.com/booksweb/GenExtSearch.aspx",
    feeScheduleUrl:
      "https://www.wake.gov/departments-government/register-deeds/recording-and-real-estate/recording-and-document-fees",
    quirk:
      "An easement naming more than twenty indexed parties picks up an excessive recording data surcharge for each additional name under N.C.G.S. § 161-10. Worth knowing on subdivision-wide utility grants.",
    sources: [
      {
        label: "Wake County Register of Deeds. Recording and document fees",
        url: "https://www.wake.gov/departments-government/register-deeds/recording-and-real-estate/recording-and-document-fees",
      },
      {
        label: "Wake County Register of Deeds. Electronic recording",
        url: "https://www.wake.gov/departments-government/register-deeds/recording-and-real-estate/electronic-recording-requirements",
      },
    ],
  },
  {
    slug: "mecklenburg",
    stateCode: "NC",
    office: "Mecklenburg County Register of Deeds",
    feeStructure:
      "A base fee covering the first thirty-five pages of a deed of trust or the first fifteen pages of other instruments, then a per-page charge, with plats priced per sheet, all set by N.C.G.S. § 161-10.",
    formatting: [
      "Letter or legal size paper, single sided.",
      "A three-inch blank margin at the top of the first page and at least half-inch margins elsewhere, as G.S. 161-14(b)(2) requires. The county's own standards page lists quarter-inch margins, so use the half-inch statutory figure.",
      "Black print on white paper, minimum nine point type.",
      "The instrument type stated at the top of page one; non-standard documents carry an added fee.",
    ],
    eRecording: true,
    submission:
      "Charlotte filings are e-recorded under a submitter agreement with the Register of Deeds, or presented at the counter.",
    parcelViewerUrl: "https://maps.mecklenburgcountync.gov/",
    indexUrl: "https://deeds.mecknc.gov/",
    feeScheduleUrl: "https://deeds.mecknc.gov/Uniform-Fee-Schedule",
    quirk:
      "Mecklenburg records satisfactions of security instruments at no fee, so releasing a recorded encumbrance costs nothing to file here.",
    sources: [
      { label: "Mecklenburg Register of Deeds. Uniform fee schedule", url: "https://deeds.mecknc.gov/Uniform-Fee-Schedule" },
      { label: "Mecklenburg Register of Deeds. Recording standards", url: "https://deeds.mecknc.gov/Document-Recording-Standards" },
    ],
  },
  {
    slug: "franklin",
    stateCode: "OH",
    office: "Franklin County Recorder",
    feeStructure:
      "Ohio prices the first two pages at a base rate under R.C. 317.32 and then charges per additional page, so exhibit-heavy easements cost more here than in a flat-fee state.",
    formatting: [
      "Ten point or larger type in blue or black ink, with no highlighting.",
      "Letter or legal size paper, one-inch side and bottom margins, a three-inch top margin on page one and one and a half inches on the rest, as R.C. 317.114 requires.",
      "A legal description on initial recordings.",
      "A preparer name and address plus a return-to address.",
    ],
    eRecording: true,
    submission:
      "Columbus filings are accepted at the counter, by mail, or electronically through third-party providers; individuals are limited to one provider and cannot transfer property electronically.",
    parcelViewerUrl: "https://gis.franklincountyohio.gov/parcelviewer/",
    indexUrl: "https://recorder.franklincountyohio.gov/",
    feeScheduleUrl: "https://recorder.franklincountyohio.gov/Resources/Fees",
    quirk:
      "A document that misses the formatting standards is recorded with a non-standardization surcharge, and a mailed filing without a self-addressed stamped envelope picks up a small extra charge.",
    sources: [
      { label: "Franklin County Recorder. Fees", url: "https://recorder.franklincountyohio.gov/Resources/Fees" },
      {
        label: "Franklin County Recorder. Recording standards",
        url: "https://www.franklincountyohio.gov/Agency-Directory/Recorder/Real-Estate/Real-Estate-Recording/Recording-Standards",
      },
    ],
  },
  {
    slug: "hamilton",
    stateCode: "OH",
    office: "Hamilton County Recorder",
    feeStructure:
      "A base charge covering the first two pages, then a per-page charge for the rest, at rates set by the Ohio General Assembly.",
    formatting: [
      "Minimum ten point type, black or blue ink only, no highlighting.",
      "Paper between letter and legal size, with one-inch side and bottom margins.",
      "A three-inch top margin on page one and an inch and a half on subsequent pages, under R.C. 317.114.",
      "Documents that miss the standards record with an added surcharge.",
    ],
    eRecording: true,
    submission:
      "Cincinnati filings can be e-recorded through the Recorder's approved providers, mailed, or presented downtown.",
    indexUrl: "https://acclaim-web.hamiltoncountyohio.gov/AcclaimWebLive",
    feeScheduleUrl: "https://recordersoffice.hamilton-co.org/fees/main_office.html",
    quirk:
      "Releases and assignments must carry a property description sufficient to index the parcel, the permanent parcel number or section/lot description, under R.C. 5301.46; a release that only cites the original book and page is rejected.",
    sources: [
      { label: "Hamilton County Recorder. Fees", url: "https://recordersoffice.hamilton-co.org/fees/main_office.html" },
      {
        label: "Hamilton County Recorder. Document standardization guidelines",
        url: "https://recordersoffice.hamilton-co.org/helpful_information/document_standardization_guidelines.html",
      },
      {
        label: "Hamilton County Recorder. ERecording",
        url: "https://recordersoffice.hamilton-co.org/helpful_information/erecording_info.html",
      },
    ],
  },
  {
    slug: "philadelphia",
    stateCode: "PA",
    office: "Philadelphia Department of Records",
    feeStructure:
      "Philadelphia charges a flat fee per document type rather than by the page, so page count rarely changes what an easement costs to record.",
    formatting: [
      "White letter-size paper, ten point black ink, printed on one side.",
      "A three-inch top margin on page one with a prepared-by and return-to block on the left, and one-inch margins elsewhere.",
      "A metes-and-bounds legal description plus the street address.",
      "A notary acknowledgment naming the state, county, date, notary signature and commission expiration.",
    ],
    eRecording: true,
    submission:
      "Businesses record electronically through a third-party service; individuals record by mail or in person at the Department of Records.",
    parcelViewerUrl: "https://atlas.phila.gov/",
    indexUrl: "https://epayss.phila-records.com/web",
    feeScheduleUrl:
      "https://www.phila.gov/departments/department-of-records/document-recording-and-service-fees/",
    quirk:
      "Philadelphia applies a deed name-match rule: the granting party's name has to match the name on the prior recorded deed, or the filing needs supporting documentation before it records.",
    sources: [
      {
        label: "Philadelphia Department of Records. Recording fees",
        url: "https://www.phila.gov/departments/department-of-records/document-recording-and-service-fees/",
      },
      {
        label: "Philadelphia. Record a deed or other document",
        url: "https://www.phila.gov/services/property-lots-housing/record-a-deed-or-other-document/",
      },
    ],
  },
  {
    slug: "allegheny",
    stateCode: "PA",
    office: "Allegheny County Department of Real Estate",
    feeStructure:
      "A flat fee per document type, with a $5 charge for each indexed name past thirty and, on deed filings, $10 for each certified parcel past thirty.",
    formatting: [
      "Letter or legal size white or manila single sheets. No continuous paper.",
      "Fully typed in black ink at ten point or larger; handwriting only in signatures and acknowledgments.",
      "A one-inch top margin left clear for the certification stamp, with a typed record-and-mail-to block below it.",
      "No tape, glue, staples or highlighting.",
    ],
    eRecording: true,
    submission:
      "Most Pittsburgh documents can be recorded online; counter and mail filing remain available at the Department of Real Estate.",
    parcelViewerUrl: "https://realestate.alleghenycounty.us/Search",
    indexUrl: "https://pa_allegheny.uslandrecords.com/palr/",
    feeScheduleUrl:
      "https://www.alleghenycounty.us/Services/Property-Assessments-and-Real-Estate/Recording-Documents/Fee-Schedule",
    quirk:
      "Allegheny rejects blanket documents that reference other filings only by volume and page, and an acknowledgment missing the county, state, date, signature or stamp is treated as defective on its face.",
    sources: [
      {
        label: "Allegheny County. Recording fee schedule",
        url: "https://www.alleghenycounty.us/Services/Property-Assessments-and-Real-Estate/Recording-Documents/Fee-Schedule",
      },
      {
        label: "Allegheny County. Formatting specifications",
        url: "https://www.alleghenycounty.us/Services/Property-Assessments-and-Real-Estate/Recording-Documents/Formatting-Specifications",
      },
      {
        label: "Allegheny County. Content requirements",
        url: "https://www.alleghenycounty.us/Services/Property-Assessments-and-Real-Estate/Recording-Documents/Content-Requirements",
      },
    ],
  },
  {
    slug: "fairfax",
    stateCode: "VA",
    office: "Land Records Division, Fairfax Circuit Court",
    feeStructure:
      "Virginia charges a tiered clerk's fee by page-count band, one to ten pages, eleven to thirty, thirty-one and up, on top of state and county recordation taxes.",
    formatting: [
      "A cover sheet carrying a two-dimensional barcode with grantor and grantee names, consideration or value, any exemption citation, the tax map identifier and the return name and address, under Va. Code § 17.1-227.1.",
      "A legal description of the land burdened by the easement.",
      "Any recordation tax exemption cited on the first page.",
    ],
    eRecording: true,
    submission:
      "Fairfax takes electronic filings through the Circuit Court's electronic filing system, except multi-jurisdiction documents and highway plats, which are recorded on paper.",
    parcelViewerUrl: "https://www.fairfaxcounty.gov/maps/",
    indexUrl: "https://www.fairfaxcounty.gov/circuit/land-records/general-information",
    feeScheduleUrl:
      "https://www.fairfaxcounty.gov/circuit/sites/circuit/files/assets/documents/pdf/fees-land-records.pdf",
    quirk:
      "Plats up to legal size record free with the instrument, while oversized plat sheets are charged per sheet. A real consideration when an easement exhibit is drawn at full scale.",
    sources: [
      {
        label: "Fairfax Circuit Court. Recordation requirements",
        url: "https://www.fairfaxcounty.gov/circuit/sites/circuit/files/Assets/Documents/PDF/land-records-recordation-requirements.pdf",
      },
      {
        label: "Fairfax Circuit Court. Land records fees",
        url: "https://www.fairfaxcounty.gov/circuit/sites/circuit/files/assets/documents/pdf/fees-land-records.pdf",
      },
      {
        label: "Fairfax Circuit Court. Land records general information",
        url: "https://www.fairfaxcounty.gov/circuit/land-records/general-information",
      },
    ],
  },
  {
    slug: "denver",
    stateCode: "CO",
    office: "Denver Clerk and Recorder, Recording Division",
    feeStructure:
      "Denver records through the combined city and county Clerk and Recorder, which publishes the current recording charges and statutory surcharges on its recording page.",
    formatting: [
      "Confirm margins and paper size against the Recording Division's published requirements before filing; the office lists what it will and will not accept.",
    ],
    eRecording: true,
    submission:
      "The Clerk and Recorder encourages electronic recording through authorized submitters, and also takes documents by mail and at the counter.",
    parcelViewerUrl: "https://www.denvergov.org/maps/map/assessormaps",
    indexUrl:
      "https://denvergov.org/Government/Agencies-Departments-Offices/Agencies-Departments-Offices-Directory/Denver-Clerk-and-Recorder/Recording-Division/find-records",
    quirk:
      "Denver cannot reject or refund a document sent to the wrong county, so an easement on land just outside the city limits that lands here is simply lost fee money. Check the jurisdiction first.",
    sources: [
      {
        label: "Denver Clerk and Recorder. Document recording",
        url: "https://www.denvergov.org/Government/Agencies-Departments-Offices/Agencies-Departments-Offices-Directory/Denver-Clerk-and-Recorder/Recording-Division/Denver-Recording/document-recording",
      },
      { label: "Denver. Property records", url: "https://www.denvergov.org/Property" },
    ],
  },
  {
    slug: "salt-lake",
    stateCode: "UT",
    office: "Salt Lake County Recorder",
    feeStructure:
      "Utah fixes a flat statutory fee per document for standard instruments, so page count does not drive the cost of recording an easement here.",
    formatting: [
      "White letter-size paper, printed on one side only and not continuously bound.",
      "A blank two and a half by four and a half inch block in the upper-right corner of page one for the recorder's stamp, and a one-inch top margin on later pages.",
      "One-inch left, right and bottom margins after the first page, black ink, no more than seven lines per vertical inch.",
      "A legal description, the grantee's name and address, and a notary acknowledgment.",
    ],
    eRecording: true,
    submission:
      "Salt Lake publishes electronic recording standards under Utah's Uniform Real Property Electronic Recording Act, alongside counter and mail filing.",
    parcelViewerUrl: "https://apps.saltlakecounty.gov/assessor/new/ParcelViewer/",
    feeScheduleUrl:
      "https://www.saltlakecounty.gov/globalassets/1-site-files/recorder/for-industry/2024-recording-requirements--fees-rev.pdf",
    quirk:
      "Every name that appears in a signature has to be typed or printed on the document as well under Utah Code § 17-71-403. An illegible signature block alone will not do.",
    sources: [
      {
        label: "Salt Lake County Recorder. Recording requirements",
        url: "https://www.saltlakecounty.gov/recorder/industry/requirements/",
      },
      {
        label: "Salt Lake County Recorder. Recording requirements and fees",
        url: "https://www.saltlakecounty.gov/globalassets/1-site-files/recorder/for-industry/2024-recording-requirements--fees-rev.pdf",
      },
    ],
  },
  {
    slug: "los-angeles",
    stateCode: "CA",
    office: "Los Angeles County Registrar-Recorder/County Clerk",
    feeStructure:
      "A first-page charge plus a per-page charge, with statutory add-ons layered on top, including the state building homes and jobs fee, the restrictive covenant modification fee, indexing, and surcharges for non-conforming page sizes or small print.",
    formatting: [
      "Letter-size pages; anything else draws an extra per-page non-conforming fee.",
      "The top two and a half inches of page one are reserved. The left three and a half inches for the return-address block, the rest for the Recorder's stamp.",
      "At least a half-inch blank margin down both sides of every page.",
      "The document must name the party requesting recording and give a return name and address, and signatures must be originals.",
    ],
    eRecording: false,
    submission:
      "Los Angeles takes documents at its recording offices and by mail. The Registrar-Recorder does not publish an online real estate index at all: records are viewed by appointment in person or requested by mail.",
    parcelViewerUrl: "https://portal.assessor.lacounty.gov/mapsearch",
    indexUrl: "https://www.lavote.gov/home/recorder/real-estate-records/viewing-real-estate-records",
    feeScheduleUrl: "https://www.lavote.gov/home/recorder/property-document-recording/fees",
    quirk:
      "Los Angeles keeps its real estate index offline entirely, citing Government Code § 6254.21. Checking a recorded easement in LA means an in-person visit or a mail request. The county's free map search shows parcels, not documents.",
    sources: [
      {
        label: "LA County RR/CC. Recording requirements",
        url: "https://www.lavote.gov/home/recorder/property-document-recording/recording-requirements",
      },
      { label: "LA County RR/CC. Fees", url: "https://www.lavote.gov/home/recorder/property-document-recording/fees" },
      {
        label: "LA County RR/CC. Viewing real estate records",
        url: "https://www.lavote.gov/home/recorder/real-estate-records/viewing-real-estate-records",
      },
    ],
  },
  {
    slug: "san-diego",
    stateCode: "CA",
    office: "San Diego County Assessor/Recorder/County Clerk",
    feeStructure:
      "A first-page charge plus a per-page charge, tiered by document title, with add-ons for the state housing fee, non-conforming page sizes and penalty print.",
    formatting: [
      "A half-inch margin down both vertical sides, with the top two and a half inches of page one reserved for recording information.",
      "A recording-requested-by and mail-to block naming the requesting party and return address.",
      "Text that reproduces photographically. Faint or shaded exhibits are refused.",
      "The land must lie in San Diego County and the instrument must be one the law authorizes to be recorded.",
    ],
    eRecording: false,
    submission:
      "Documents are recorded at the Assessor/Recorder/County Clerk's branch offices or by mail. Name and document-number searches run online; parcel-number searches do not.",
    parcelViewerUrl: "https://www.sangis.org/pages/interactive-map",
    indexUrl: "https://www.sdarcc.gov/content/arcc/home/divisions/recorder-clerk/recording.html",
    feeScheduleUrl:
      "https://www.sdarcc.gov/content/dam/arcc/recorder-county-clerk/forms/Recorder%20County%20Clerk%20Fee%20Schedule_Changes%20Effective%2020260101.pdf",
    quirk:
      "Since December 9, 2024 San Diego has removed parcel-number searching from its online official records index under California AB 1785, so an APN-based easement search has to be run on a kiosk at one of the county offices.",
    sources: [
      {
        label: "San Diego ARCC. Recording",
        url: "https://www.sdarcc.gov/content/arcc/home/divisions/recorder-clerk/recording.html",
      },
      {
        label: "San Diego ARCC. Fee schedule",
        url: "https://www.sdarcc.gov/content/dam/arcc/recorder-county-clerk/forms/Recorder%20County%20Clerk%20Fee%20Schedule_Changes%20Effective%2020260101.pdf",
      },
    ],
  },
  {
    slug: "santa-clara",
    stateCode: "CA",
    office: "Santa Clara County Clerk-Recorder",
    feeStructure:
      "A first-page charge plus a per-page charge, with per-title, indexing, non-conforming size and small-print surcharges on top.",
    formatting: [
      "California's statewide recording format rules apply; the Clerk-Recorder publishes its own document requirements page. Confirm margins against it before filing.",
    ],
    eRecording: false,
    submission:
      "San Jose filings are recorded in person or by mail. The office does not run record searches on the public's behalf.",
    parcelViewerUrl: "https://gis.santaclaracounty.gov/access-geospatial-information-sccmap",
    indexUrl: "https://clerkrecorder.santaclaracounty.gov/official-records/records-search",
    feeScheduleUrl:
      "https://clerkrecorder.santaclaracounty.gov/recording-documents/recording-document-fees",
    quirk:
      "Santa Clara stopped offering an online official records index in 2018 on privacy grounds, so tracing a Silicon Valley easement chain means visiting the office or writing for copies.",
    sources: [
      {
        label: "Santa Clara Clerk-Recorder. Recording fees",
        url: "https://clerkrecorder.santaclaracounty.gov/recording-documents/recording-document-fees",
      },
      {
        label: "Santa Clara Clerk-Recorder. Records search",
        url: "https://clerkrecorder.santaclaracounty.gov/official-records/records-search",
      },
    ],
  },
  {
    slug: "king",
    stateCode: "WA",
    office: "King County Recorder's Office",
    feeStructure:
      "Washington sets recording charges by statute at RCW 36.18.010, priced off page count rather than as a single flat fee.",
    formatting: [
      "Pages no larger than eight and a half by fourteen inches, with type no smaller than eight point.",
      "A three-inch top margin on page one and one-inch side and bottom margins, or one-inch margins all round when a cover sheet is used.",
      "Page one must carry the title, the grantors and grantees, the legal description and the tax parcel number; a cover sheet is required when any of that is missing.",
      "Raised notary seals must be smudged so they image legibly when scanned.",
    ],
    eRecording: true,
    submission:
      "Seattle-area documents can be e-recorded, mailed, or brought to the Recorder's Office, which lists each submission method on its recording page.",
    parcelViewerUrl: "https://gismaps.kingcounty.gov/parcelviewer2/",
    indexUrl: "https://recordsearch.kingcounty.gov/LandmarkWeb",
    feeScheduleUrl:
      "https://kingcounty.gov/en/dept/executive-services/certificates-permits-licenses/records-licensing/recorders-office/document-recording",
    quirk:
      "The smudged-seal rule is a genuine rejection trigger in Washington: an embossed notary seal that does not show up in the scan sends the easement back.",
    sources: [
      {
        label: "King County Recorder. Document recording",
        url: "https://kingcounty.gov/en/dept/executive-services/certificates-permits-licenses/records-licensing/recorders-office/document-recording",
      },
      {
        label: "King County Recorder. Formatting requirements",
        url: "https://cdn.kingcounty.gov/-/media/king-county/depts/executive-services/records-licensing/recorders-office/requirements-formatting.pdf",
      },
      { label: "King County. Official records search", url: "https://recordsearch.kingcounty.gov/LandmarkWeb" },
    ],
  },
  {
    slug: "cook",
    stateCode: "IL",
    office: "Cook County Clerk's Office, Recordings Division",
    feeStructure:
      "Since April 1, 2024 Cook County prices recordings by document class rather than by the page, with separate pricing for irregular or blanket documents covering six or more parcel index numbers, and separate schedules for liens and plats.",
    formatting: [
      "A mail-to name and address plus the name and address of the person who prepared the document.",
      "The common street address, the Property Index Number and the legal description of the land.",
      "Ten point type or larger so the document images legibly.",
      "Notarization where required, with every exhibit marked as an exhibit.",
    ],
    eRecording: true,
    submission:
      "Chicago filings can be mailed or e-recorded, but the Clerk excludes condominium declarations, plats, and documents carrying more than five parcel index numbers or prior document numbers from electronic submission.",
    parcelViewerUrl: "https://maps.cookcountyil.gov/cookviewer/",
    indexUrl: "https://crs.cookcountyclerkil.gov/Search",
    feeScheduleUrl: "https://www.cookcountyclerkil.gov/recordings/recording-fees",
    quirk:
      "Easements, plats, condominium declarations and dedications sit outside Cook County's online copy purchase and e-recording paths, so the very documents you most want are the ones you often have to order the old-fashioned way.",
    sources: [
      { label: "Cook County Clerk. Recording fees", url: "https://www.cookcountyclerkil.gov/recordings/recording-fees" },
      {
        label: "Cook County Clerk. Document requirements",
        url: "https://www.cookcountyclerk.com/recordings/document-requirements",
      },
      {
        label: "Cook County Clerk. Mail and e-record deeds",
        url: "https://www.cookcountyclerkil.gov/recordings/how-to-mail-and-e-record-deeds",
      },
    ],
  },
  {
    slug: "dupage",
    stateCode: "IL",
    office: "DuPage County Recorder",
    feeStructure:
      "DuPage charges one flat fee per document class, deeds, mortgages and easements each sit in a class, with a higher flat fee for non-standard documents and a separate rate for maps and plats. Page count does not drive the price.",
    formatting: [
      "Illinois statutory format rules apply, and a document that misses them records at the higher non-standard flat rate rather than being refused.",
      "Plats follow the county's separate published plat recording requirements.",
    ],
    eRecording: true,
    submission:
      "Naperville-area filings can be submitted through the Recorder's approved e-recording vendors, or by mail and at the counter in Wheaton. Plats and military discharges are not eligible for e-recording.",
    parcelViewerUrl: "https://gis.dupageco.org/parcelviewer/",
    indexUrl: "https://www.dupagecounty.gov/elected_officials/recorder/search_records_online.php",
    feeScheduleUrl: "https://www.dupagecounty.gov/elected_officials/recorder/fee_schedule.php",
    quirk:
      "Every standard DuPage recording fee bundles a GIS surcharge that funds the county's mapping program. Which is part of why the free DuPage parcel viewer is unusually good for locating a recorded strip.",
    sources: [
      { label: "DuPage County Recorder. Fee schedule", url: "https://www.dupagecounty.gov/elected_officials/recorder/fee_schedule.php" },
      { label: "DuPage County Recorder. Online eRecording", url: "https://www.dupagecounty.gov/elected_officials/recorder/online-recording.php" },
      { label: "DuPage County Recorder. Search records online", url: "https://www.dupagecounty.gov/elected_officials/recorder/search_records_online.php" },
    ],
  },
];

const BY_KEY = new Map(
  COUNTY_RECORDING_RULES.map((rule) => [`${rule.stateCode}:${rule.slug}`, rule]),
);

export function countyRecordingRuleFor(
  stateCode: string,
  slug: string,
): CountyRecordingRule | null {
  return BY_KEY.get(`${stateCode.toUpperCase()}:${slug}`) ?? null;
}

/** Counties with a researched, sourced recording profile, for coverage copy. */
export function countyRecordingRuleCount(stateCode?: string): number {
  if (!stateCode) return COUNTY_RECORDING_RULES.length;
  return COUNTY_RECORDING_RULES.filter(
    (rule) => rule.stateCode === stateCode.toUpperCase(),
  ).length;
}
